import * as THREE from "three";

export interface LoomPickTarget {
  nodeId: string;
  worldYMax: number;
  worldYMin: number;
  x: number;
  y: number;
}

export function isLoomObjectEffectivelyVisible(object: THREE.Object3D): boolean {
  for (let current: THREE.Object3D | null = object; current; current = current.parent) {
    if (!current.visible) return false;
  }
  return true;
}

export function resolveLoomViewportPickNodeId(objects: readonly THREE.Object3D[]): string | null {
  const editable = objects.find((object) => isLoomObjectEffectivelyVisible(object)
    && typeof object.userData.loomEditableNodeId === "string");
  const nodeId = editable?.userData.loomEditableNodeId;
  return typeof nodeId === "string" ? nodeId : null;
}

function pickNodeAtNdc(
  scene: THREE.Group,
  camera: THREE.PerspectiveCamera,
  raycaster: THREE.Raycaster,
  x: number,
  y: number,
): string | null {
  raycaster.setFromCamera(new THREE.Vector2(x, y), camera);
  return resolveLoomViewportPickNodeId(
    raycaster.intersectObjects(scene.children, true).map(({ object }) => object),
  );
}

export function resolveLoomViewportPickAtCanvasPoint(
  scene: THREE.Group,
  camera: THREE.PerspectiveCamera,
  x: number,
  y: number,
  width: number,
  height: number,
): string | null {
  if (width <= 0 || height <= 0) return null;
  return pickNodeAtNdc(
    scene, camera, new THREE.Raycaster(), (x / width) * 2 - 1, 1 - (y / height) * 2,
  );
}

export function deriveVisiblePickTargets(
  scene: THREE.Group,
  camera: THREE.PerspectiveCamera,
  width: number,
  height: number,
): LoomPickTarget[] {
  const raycaster = new THREE.Raycaster();
  const best = new Map<string, { score: number; target: LoomPickTarget }>();
  scene.traverse((object) => {
    const mesh = object as THREE.Mesh;
    const nodeId = mesh.userData.loomEditableNodeId;
    const positions = mesh.geometry?.getAttribute("position");
    if (!mesh.isMesh || !isLoomObjectEffectivelyVisible(mesh)
      || typeof nodeId !== "string" || !positions) return;
    const bounds = new THREE.Box3().setFromObject(mesh);
    const index = mesh.geometry.index;
    const triangleCount = Math.floor((index?.count ?? positions.count) / 3);
    const stride = Math.max(1, Math.ceil(triangleCount / 24));
    for (let triangle = 0; triangle < triangleCount; triangle += stride) {
      const point = new THREE.Vector3();
      for (let corner = 0; corner < 3; corner += 1) {
        const offset = triangle * 3 + corner;
        const vertex = index ? index.getX(offset) : offset;
        point.x += positions.getX(vertex);
        point.y += positions.getY(vertex);
        point.z += positions.getZ(vertex);
      }
      point.multiplyScalar(1 / 3);
      mesh.localToWorld(point);
      const projected = point.clone().project(camera);
      if (Math.abs(projected.x) >= 0.98 || Math.abs(projected.y) >= 0.98
        || projected.z <= -1 || projected.z >= 1) continue;
      if (pickNodeAtNdc(scene, camera, raycaster, projected.x, projected.y) !== nodeId) continue;
      const x = ((projected.x + 1) / 2) * width;
      const y = ((1 - projected.y) / 2) * height;
      const score = Math.min(x, width - x, y, height - y);
      const existing = best.get(nodeId);
      const worldYMin = Math.min(existing?.target.worldYMin ?? Infinity, bounds.min.y);
      const worldYMax = Math.max(existing?.target.worldYMax ?? -Infinity, bounds.max.y);
      if (score > (existing?.score ?? -1)) {
        best.set(nodeId, { score, target: { nodeId, x, y, worldYMin, worldYMax } });
      } else if (existing) existing.target = { ...existing.target, worldYMin, worldYMax };
    }
  });
  return [...best.values()].map(({ target }) => target);
}
